import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the server limit
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic'];

const REASONS = [
  { value: 'sold', label: 'Sold', docs: ['Bill of Sale', 'Cancelled Registration'] },
  { value: 'traded', label: 'Traded', docs: ['Trade-in Documentation', 'Cancelled Registration'] },
  { value: 'stolen', label: 'Stolen', docs: ['Police Report', 'Insurance Documentation'] },
  { value: 'total_loss', label: 'Total Loss', docs: ['Insurance Total Loss Documentation', 'Cancelled Registration'] },
  { value: 'moved_from_town', label: 'Moved from this town (before January 1 of tax year)', docs: ['Proof of New Address'] },
  { value: 'moved_from_state', label: 'Moved from Massachusetts (as of January 1 of tax year)', docs: ['Proof of Out-of-State Registration or Address'] },
  { value: 'junked', label: 'Junked', docs: ['Junk Certificate', 'Cancelled Registration'] },
  { value: 'repossessed', label: 'Repossessed', docs: ['Documentation from Lender'] },
  { value: 'exempt', label: 'Exempt (veteran, blind, etc.)', docs: ['Exemption Documentation'] },
  { value: 'other', label: 'Other', docs: ['Supporting Documentation'] },
];

function ExciseAbatementPage2() {
  const params = new URLSearchParams(window.location.search);
  const town = params.get('town');
  const townCode = params.get('town_code');

  const [page1Data, setPage1Data] = useState(null);
  const [reason, setReason] = useState('');
  const [otherDetail, setOtherDetail] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const saved = sessionStorage.getItem('exciseAbatementPage1');
    if (!saved) {
      window.location.href = `/excise-abatement?town=${town}&town_code=${townCode}`;
      return;
    }
    setPage1Data(JSON.parse(saved));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedReason = REASONS.find(r => r.value === reason);

  // calculate months remaining after event month
  const calcMonths = () => {
    if (!eventDate) return null;
    const month = new Date(eventDate + 'T00:00:00').getMonth() + 1;
    return 12 - month;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!reason) { setError('Please select a reason for your abatement request.'); return; }
    if (reason === 'other' && !otherDetail) { setError('Please describe your reason.'); return; }
    if (!eventDate) { setError('Please enter the date of the qualifying event.'); return; }
    if (!signatureName) { setError('Please enter your full name as your e-signature.'); return; }
    if (!agreedToTerms) { setError('You must certify that the information provided is true and correct.'); return; }

    await submitAbatement();
  };

  const submitAbatement = async () => {
    setLoading(true);
    try {
      const page2Saved = sessionStorage.getItem('exciseAbatementPage2');
      const page2Data = page2Saved ? JSON.parse(page2Saved) : { reason, otherDetail, signatureName, eventDate };

      const payload = {
  town_code: page1Data.townCode,
  owner_name: page1Data.ownerName,
  bill_number: page1Data.billNumber,
  bill_year: page1Data.billYear,
  plate_number: page1Data.plateNumber,
  address_on_bill: page1Data.addressOnBill,
  mailing_address: page1Data.mailingAddress || null,
  phone: page1Data.phone,
  email: page1Data.email,
  reason: page2Data.reason || reason,
  other_reason_detail: page2Data.otherDetail || otherDetail || null,
  event_date: eventDate,
  signature_name: page2Data.signatureName || signatureName,
  signed_at: new Date().toISOString(),
};

      // send the login token if there is one, so the abatement shows up on the taxpayer's My Filings page
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/excise/submit`, payload,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);

      sessionStorage.removeItem('exciseAbatementPage1');
      sessionStorage.removeItem('exciseAbatementPage2');
      setSuccess(response.data);

      if (files.length > 0) {
        await uploadDocuments(response.data.id, response.data.upload_token);
      }
    } catch (err) {
      const errDetail = err.response?.data?.detail;
setError(typeof errDetail === 'string' ? errDetail : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const uploadDocuments = async (abatementId, uploadToken) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/excise/${abatementId}/documents`,
        formData,
        { headers: { 'X-Upload-Token': uploadToken } } // pass from /submit, good for this filing only
      );
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      setUploadError(typeof errDetail === 'string' ? errDetail : 'Your application was submitted, but the document upload failed. Please contact the assessor\'s office.');
    }
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files);
    // same limits the server enforces, checked now so nobody finds out after submitting
    const tooBig = picked.filter(f => f.size > MAX_FILE_SIZE);
    const wrongType = picked.filter(f => !ALLOWED_EXTENSIONS.includes(f.name.slice(f.name.lastIndexOf('.')).toLowerCase()));
    if (tooBig.length) window.alert(`This file is too large (10 MB max):\n${tooBig.map(f => f.name).join('\n')}`);
    if (wrongType.length) window.alert(`Please upload a photo or PDF:\n${wrongType.map(f => f.name).join('\n')}`);
    const newFiles = picked.filter(f => !tooBig.includes(f) && !wrongType.includes(f));
    setFiles(prev => {
      const combined = [...prev, ...newFiles];
      const seen = new Set();
      return combined.filter(f => {
        const key = `${f.name}-${f.size}-${f.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // success screen
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>MAtownforms</h1>
          <div style={styles.successBox}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Application Submitted</h2>
            <p style={styles.successText}>Your motor vehicle excise abatement application has been received.</p>
            <div style={styles.refBox}>
              <p style={styles.refLabel}>Reference Number</p>
              <p style={styles.refNumber}>{success.filing_reference}</p>
            </div>
            <p style={styles.successNote}>
              The assessor's office will review your application. Please save this reference number.
            </p>
            {uploadError && <p style={styles.error}>{uploadError}</p>}
            <button style={styles.button} onClick={() => window.location.href = '/my-filings'}>
              Back to My Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  const months = calcMonths();

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        <div style={styles.header}>
          <h1 style={styles.title}>MAtownforms</h1>
          <div style={styles.stepRow}>
            <div style={styles.stepDone}>✓ Filer Info</div>
            <div style={styles.stepLine}/>
            <div style={styles.stepActive}>2 Reason & Sign</div>
          </div>
        </div>

        <p style={styles.meta}>Motor Vehicle Excise Abatement &nbsp;|&nbsp; {town}</p>
        <h2 style={styles.subtitle}>Step 2 — Reason & Signature</h2>

        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleSubmit}>

          {/* Reason selection */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>Reason for Abatement *</label>
            <div style={styles.reasonGrid}>
              {REASONS.map(r => (
                <div
                  key={r.value}
                  style={reason === r.value ? styles.reasonSelected : styles.reason}
                  onClick={() => setReason(r.value)}
                >
                  <div style={styles.reasonRadio}>
                    <div style={reason === r.value ? styles.radioDot : styles.radioEmpty} />
                  </div>
                  <span style={styles.reasonLabel}>{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Other detail */}
          {reason === 'other' && (
            <div style={styles.field}>
              <label style={styles.label}>Please describe your reason *</label>
              <textarea
                style={styles.textarea}
                value={otherDetail}
                onChange={e => setOtherDetail(e.target.value)}
                rows={3}
                required
              />
            </div>
          )}

          {/* Date of qualifying event */}
          {reason && (
            <div style={styles.field}>
              <label style={styles.label}>Date of Qualifying Event *</label>
              <input
                style={styles.input}
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
                required
              />
            </div>
          )}

          {/* Months calculation */}
          {eventDate && (
            <div style={styles.calcBox}>
              <p style={styles.calcResult}>
                <strong>{months} month{months !== 1 ? 's' : ''}</strong> estimated abatement
              </p>
            </div>
          )}

          {/* Required documents notice */}
          {selectedReason && (
            <div style={styles.docsBox}>
              <p style={styles.docsTitle}>Required Documents</p>
              <ol style={styles.numberedList}>
                {selectedReason.docs.map((doc, i) => (
                  <li key={i} style={styles.docsItem}>{doc}</li>
                ))}
              </ol>
              <div style={styles.field}>
                <label style={styles.label}>Upload Supporting Documents</label>
                <input
                  style={styles.input}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.heic"
                  onChange={handleFileChange}
                />
                {files.length > 0 && (
                  <ul style={styles.docsList}>
                    {files.map((f, i) => (
                      <li key={i} style={styles.uploadedFileItem}>
                        <span>{f.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(i)}
                          style={styles.removeFileButton}
                          aria-label={`Remove ${f.name}`}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* E-signature */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>E-Signature *</label>
            <div style={styles.field}>
              <input
                style={styles.input}
                type="text"
                value={signatureName}
                onChange={e => setSignatureName(e.target.value)}
                placeholder="Type your full name"
                required
              />
            </div>
            <div style={styles.checkRow}>
              <input
                type="checkbox"
                id="agree"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                style={styles.checkbox}
              />
              <label htmlFor="agree" style={styles.checkLabel}>
                I certify that the above information is true and correct under the pains and penalties of perjury.
              </label>
            </div>
          </div>

          <button
            style={loading ? styles.buttonDisabled : styles.button}
            type="submit"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>

        </form>

        <p style={styles.backLink}>
          <a href={`/excise-abatement?town=${town}&town_code=${townCode}`} style={styles.anchor}>← Back</a>
        </p>

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' },
  card: { backgroundColor: '#ffffff', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', width: '100%', maxWidth: '560px' },
  header: { marginBottom: '16px' },
  title: { color: '#1a3a5c', fontSize: '42px', fontWeight: '700', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', marginBottom: '16px', textAlign: 'center' },
  stepRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' },
  stepDone: { backgroundColor: '#2e7d32', color: '#fff', padding: '4px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
  stepLine: { width: '40px', height: '2px', backgroundColor: '#ccc' },
  stepActive: { backgroundColor: '#1a3a5c', color: '#fff', padding: '4px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
  meta: { textAlign: 'center', fontSize: '13px', color: '#666', marginBottom: '8px' },
  subtitle: { color: '#1a3a5c', fontSize: '18px', marginBottom: '20px', textAlign: 'center' },
  section: { marginBottom: '24px' },
  sectionLabel: { display: 'block', fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '10px' },
  reasonGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  reason: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' },
  reasonSelected: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', border: '2px solid #1a3a5c', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f0f4f8' },
  reasonRadio: { width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #1a3a5c', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  radioDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1a3a5c' },
  radioEmpty: { width: '8px', height: '8px' },
  reasonLabel: { fontSize: '14px', color: '#333' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '6px' },
  input: { width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' },
  calcBox: { backgroundColor: '#e3f0fb', border: '1px solid #90caf9', borderRadius: '6px', padding: '14px', marginBottom: '20px' },
  calcResult: { fontSize: '14px', color: '#1a3a5c', lineHeight: '1.5' },
  docsBox: { backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '14px', marginBottom: '20px' },
  docsTitle: { fontSize: '13px', fontWeight: '600', color: '#2e7d32', marginBottom: '8px' },
  docsList: { margin: '0 0 8px 0', paddingLeft: '0', listStyle: 'none' },
  numberedList: { margin: '0 0 8px 0', paddingLeft: '20px' },
  docsItem: { fontSize: '13px', color: '#333', padding: '2px 0' },
  uploadedFileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#333', padding: '4px 0' },
  removeFileButton: { background: 'none', border: 'none', color: '#cc0000', cursor: 'pointer', fontSize: '13px', padding: '0 4px' },
  docsNote: { fontSize: '12px', color: '#555', fontStyle: 'italic', marginTop: '8px' },
  checkRow: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  checkbox: { marginTop: '2px', flexShrink: 0 },
  checkLabel: { fontSize: '13px', color: '#333', lineHeight: '1.5' },
  button: { width: '100%', padding: '12px', backgroundColor: '#1a3a5c', color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer', marginBottom: '16px' },
  buttonDisabled: { width: '100%', padding: '12px', backgroundColor: '#aaa', color: '#ffffff', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'not-allowed', marginBottom: '16px' },
  error: { color: '#cc0000', fontSize: '14px', marginBottom: '16px', textAlign: 'center' },
  backLink: { textAlign: 'center', fontSize: '14px' },
  anchor: { color: '#1a3a5c', fontWeight: '600' },
  successBox: { textAlign: 'center', padding: '20px 0' },
  successIcon: { fontSize: '48px', color: '#2e7d32', marginBottom: '12px' },
  successTitle: { color: '#1a3a5c', fontSize: '22px', marginBottom: '8px' },
  successText: { fontSize: '14px', color: '#555', marginBottom: '20px' },
  refBox: { backgroundColor: '#f4f6f9', padding: '16px', borderRadius: '6px', marginBottom: '16px' },
  refLabel: { fontSize: '12px', color: '#888', marginBottom: '4px' },
  refNumber: { fontSize: '20px', fontWeight: '700', color: '#1a3a5c', fontFamily: 'monospace' },
  successNote: { fontSize: '13px', color: '#666', marginBottom: '24px', lineHeight: '1.6' },
};

export default ExciseAbatementPage2;